"use client";

import { ChevronRight, Disc3, Pause, Pencil, Play } from "lucide-react";
import { useState } from "react";
import { ChatDrawerHeader } from "@/components/chat/chat-ui-primitives";
import { PlayingBars } from "@/components/music/playing-bars";
import { SongSheet } from "@/components/music/song-sheet";
import { useMusicPreview } from "@/components/music/use-music-preview";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { musicAttachmentFromProfileSong } from "@/lib/music/attachment";
import { musicArtworkSrc } from "@/lib/music/links";
import {
  PROFILE_SONG_FEATURED,
  type ProfileMusicSong,
} from "@/lib/music/types";
import { cn } from "@/lib/utils";

/**
 * Everything on someone's Music picks — the five on display and the rest
 * behind "+N". Every row previews in place; tapping a song opens its sheet
 * (listen on…, send, post with it, add to YOUR picks). Owners get "Edit list".
 */
export function MusicListDrawer({
  open,
  onOpenChange,
  songs,
  ownerFirstName,
  ownerUserId,
  onEdit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  songs: ProfileMusicSong[];
  ownerFirstName: string;
  ownerUserId: string;
  /** Owner only: open the picker on the list. */
  onEdit?: () => void;
}) {
  const preview = useMusicPreview();
  // The song stays set after closing so the sheet can animate out.
  const [sheetSong, setSheetSong] = useState<ProfileMusicSong | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const title =
    ownerFirstName === "My"
      ? "Your Music picks"
      : `${ownerFirstName}'s Music picks`;

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={(next) => {
          if (!next) preview.pause();
          onOpenChange(next);
        }}
      >
        <DrawerContent className="mx-auto h-[calc(80dvh-env(safe-area-inset-top))] max-w-2xl data-[vaul-drawer-direction=bottom]:rounded-t-3xl">
          <DrawerTitle className="sr-only">{title}</DrawerTitle>
          <ChatDrawerHeader
            title={title}
            subtitle={`${songs.length} ${songs.length === 1 ? "song" : "songs"}`}
          />
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pb-4">
            {songs.map((song, index) => {
              const art = musicArtworkSrc(song.artworkUrl, 96);
              const playing =
                preview.key === song.id &&
                (preview.status === "playing" || preview.status === "loading");
              return (
                <div key={song.id}>
                  {index === 0 ? (
                    <Divider label="On display" />
                  ) : index === PROFILE_SONG_FEATURED ? (
                    <Divider label="More picks" />
                  ) : null}
                  <div
                    className={cn(
                      "flex h-16 items-center gap-3 rounded-xl px-2 transition-colors duration-150",
                      playing && "bg-surface-alt dark:bg-glass-bg-10",
                    )}
                  >
                    <span className="w-5 shrink-0 text-center text-xs font-semibold text-text-tertiary tabular-nums">
                      {index + 1}
                    </span>
                    <button
                      type="button"
                      disabled={!song.previewUrl}
                      onClick={() =>
                        preview.toggle({
                          key: song.id,
                          previewUrl: song.previewUrl,
                          ownerUserId,
                          surface: "profile",
                        })
                      }
                      aria-label={
                        playing ? `Pause ${song.title}` : `Play ${song.title}`
                      }
                      className="relative size-12 shrink-0 overflow-hidden rounded-lg bg-surface-alt active:scale-95 dark:bg-glass-bg-10"
                    >
                      {art ? (
                        // biome-ignore lint/performance/noImgElement: Apple CDN, exact size requested.
                        <img
                          src={art}
                          alt=""
                          loading="lazy"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <Disc3 className="m-auto size-5 text-text-tertiary" />
                      )}
                      {song.previewUrl ? (
                        <span className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <span className="flex size-7 items-center justify-center rounded-full bg-white/95 text-neutral-900 shadow-md">
                            {playing ? (
                              <Pause
                                className="size-3.5"
                                fill="currentColor"
                                strokeWidth={0}
                              />
                            ) : (
                              <Play
                                className="ml-px size-3.5"
                                fill="currentColor"
                                strokeWidth={0}
                              />
                            )}
                          </span>
                        </span>
                      ) : null}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        preview.pause();
                        setSheetSong(song);
                        setSheetOpen(true);
                      }}
                      className="flex h-full min-w-0 flex-1 items-center gap-2 text-left"
                    >
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-sm font-medium text-text-primary">
                          {song.title}
                        </span>
                        <span className="flex items-center gap-1.5 truncate text-xs text-text-secondary">
                          {playing ? (
                            <PlayingBars
                              className="h-2.5 shrink-0 text-brand"
                              barClassName="w-[2px]"
                            />
                          ) : null}
                          <span className="truncate">{song.artistName}</span>
                        </span>
                      </span>
                      <ChevronRight className="size-4 shrink-0 text-text-tertiary" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          {onEdit ? (
            <div className="border-t border-border px-4 pt-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] dark:border-glass-border-10">
              <Button
                variant="outline"
                className="h-11 w-full gap-2 rounded-full"
                onClick={onEdit}
              >
                <Pencil className="size-4" />
                Edit list
              </Button>
            </div>
          ) : (
            <div className="pb-[env(safe-area-inset-bottom)]" />
          )}
        </DrawerContent>
      </Drawer>

      {sheetSong ? (
        <SongSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          music={musicAttachmentFromProfileSong(sheetSong)}
          nested
        />
      ) : null}
    </>
  );
}

function Divider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 px-2 pt-3 pb-1">
      <span className="text-[11px] font-bold tracking-wider text-text-tertiary uppercase">
        {label}
      </span>
      <span className="h-px flex-1 bg-border dark:bg-glass-border-10" />
    </div>
  );
}
